export type ProjectSelectorSearchWorkspace<ProjectType = unknown> = {
  workspaceId: string;
  workspaceName: string;
  projects: ProjectType[];
};

export type ProjectSelectorProjectGroup<
  WorkspaceType extends ProjectSelectorSearchWorkspace,
> = {
  workspace: WorkspaceType;
  projects: WorkspaceType["projects"];
};

type WorkspaceProject<WorkspaceType extends ProjectSelectorSearchWorkspace> =
  WorkspaceType["projects"][number];

function compareNames(first: string, second: string) {
  return first.localeCompare(second, undefined, { sensitivity: "base" });
}

export function getProjectSelectorSearchGroups<
  WorkspaceType extends ProjectSelectorSearchWorkspace,
>({
  workspaces,
  query,
  getProjectName,
  currentWorkspaceId,
}: {
  workspaces: WorkspaceType[];
  query: string;
  getProjectName: (project: WorkspaceProject<WorkspaceType>) => string;
  currentWorkspaceId?: string;
}): {
  matchingWorkspaces: WorkspaceType[];
  currentWorkspaceProjectGroup: ProjectSelectorProjectGroup<WorkspaceType> | null;
  otherProjectGroups: ProjectSelectorProjectGroup<WorkspaceType>[];
  matchingProjectGroups: ProjectSelectorProjectGroup<WorkspaceType>[];
} {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return {
      matchingWorkspaces: workspaces,
      currentWorkspaceProjectGroup: null,
      otherProjectGroups: [],
      matchingProjectGroups: [],
    };
  }

  const matchesQuery = (value: string) => {
    return value.toLowerCase().includes(normalizedQuery);
  };

  const matchingWorkspaces = workspaces
    .filter((workspace) => {
      const isCurrentWorkspace = workspace.workspaceId === currentWorkspaceId;
      return !isCurrentWorkspace && matchesQuery(workspace.workspaceName);
    })
    .sort((first, second) => {
      return compareNames(first.workspaceName, second.workspaceName);
    });

  const matchingProjectGroups = workspaces.flatMap((workspace) => {
    const matchingProjects = (workspace.projects ?? [])
      .filter((project) => {
        return matchesQuery(getProjectName(project));
      })
      .sort((first, second) => {
        return compareNames(getProjectName(first), getProjectName(second));
      });
    return matchingProjects.length > 0
      ? [{ workspace, projects: matchingProjects }]
      : [];
  });

  const currentWorkspaceProjectGroup =
    matchingProjectGroups.find((group) => {
      return group.workspace.workspaceId === currentWorkspaceId;
    }) ?? null;

  const otherProjectGroups = matchingProjectGroups
    .filter((group) => {
      return group.workspace.workspaceId !== currentWorkspaceId;
    })
    .sort((first, second) => {
      return compareNames(
        first.workspace.workspaceName,
        second.workspace.workspaceName,
      );
    });

  return {
    matchingWorkspaces,
    currentWorkspaceProjectGroup,
    otherProjectGroups,
    matchingProjectGroups,
  };
}
